/**
 * Shared line-buffering helpers for deploy targets.
 *
 * Both the local target (subprocess stdout) and the container runtimes
 * (`<bin> logs`) read text off a web `ReadableStream<Uint8Array>` and need the
 * same UTF-8 decode + newline-split logic, plus a timestamped ring buffer so
 * `DeployHandle.logs()` can replay what was already seen.
 */

/** One buffered log line with the wall-clock time it was observed. */
export interface BufferedLine {
  ts: number;
  line: string;
}

/**
 * Bounded FIFO of decoded lines. Drives both the readiness watcher and
 * `logs()` replay; the cap keeps a long-running process from growing unbounded.
 */
export class LineBuffer {
  private readonly lines: BufferedLine[] = [];
  private readonly waiters = new Set<(line: BufferedLine) => void>();

  constructor(private readonly max = 1000) {}

  push(line: string): void {
    const entry: BufferedLine = { ts: Date.now(), line };
    this.lines.push(entry);
    if (this.lines.length > this.max) this.lines.shift();
    for (const w of this.waiters) w(entry);
  }

  /** Snapshot of buffered lines at or after `sinceMs` (epoch millis). */
  since(sinceMs = 0): BufferedLine[] {
    return this.lines.filter((l) => l.ts >= sinceMs);
  }

  /** Subscribe to future pushes; returns an unsubscribe fn. */
  subscribe(fn: (line: BufferedLine) => void): () => void {
    this.waiters.add(fn);
    return () => this.waiters.delete(fn);
  }
}

/**
 * Pump a byte stream into a {@link LineBuffer}, splitting on `\n`. Resolves when
 * the stream ends. Errors (e.g. the process being killed mid-read) are
 * swallowed — a dead stream just means no more lines.
 */
export async function pumpLines(
  stream: ReadableStream<Uint8Array>,
  buffer: LineBuffer,
): Promise<void> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let pending = "";
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      pending += decoder.decode(value, { stream: true });
      let idx = pending.indexOf("\n");
      while (idx !== -1) {
        buffer.push(pending.slice(0, idx));
        pending = pending.slice(idx + 1);
        idx = pending.indexOf("\n");
      }
    }
  } catch {
    // Stream torn down (process killed) — nothing more to read.
  } finally {
    if (pending.length > 0) buffer.push(pending);
    reader.releaseLock();
  }
}

/**
 * Resolve once a buffered line matches `pattern`, reject on timeout or abort.
 * Checks already-buffered lines first so a fast-printing process never races
 * the subscription.
 */
export function waitForLine(
  buffer: LineBuffer,
  pattern: RegExp,
  opts: { timeoutMs: number; signal: AbortSignal },
): Promise<void> {
  for (const { line } of buffer.since(0)) {
    if (pattern.test(line)) return Promise.resolve();
  }
  return new Promise<void>((resolve, reject) => {
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsub();
      opts.signal.removeEventListener("abort", onAbort);
      fn();
    };
    const unsub = buffer.subscribe(({ line }) => {
      if (pattern.test(line)) finish(resolve);
    });
    const onAbort = () => finish(() => reject(new Error("aborted while waiting for ready line")));
    const timer = setTimeout(
      () =>
        finish(() =>
          reject(new Error(`timed out after ${opts.timeoutMs}ms waiting for ready line`)),
        ),
      opts.timeoutMs,
    );
    if (opts.signal.aborted) onAbort();
    else opts.signal.addEventListener("abort", onAbort);
  });
}
